/* global bocVars, BocResourceManager */

/**
 * BocDataTrigger polls valet for a specific series, dimension short and key.
 * When the data is found, BocDataTrigger will resolve the promise so that
 * the calling context can continue rendering charts and data links
 */
// eslint-disable-next-line no-unused-vars,require-jsdoc
class BocDataTrigger {

    /**
     * Stores only one promise so that subsequent calls to start use the same promise.
     *
     * @type {null|Promise}
     */
    static promise = null;

    /**
     * Creates an instance of BocDataTrigger
     * @param {string} series The series that needs to poll
     * @param {string} dimensionShort The dimension short, e.g. 'd','k'
     * @param {string} dimensionValue The dimensionValue that heeds to be present to trigger rendering on the page
     * @param {string} query Additional query string to refine valet requests
     */
    constructor(series, dimensionShort, dimensionValue, query = '') {
        let valetUrl = bocVars.valetUrl.replace(/\/$/, "");
        this.url = `${valetUrl}/observations/${series}`;
        if (query.length > 0) {
            this.url += `?${query}`;
        }
        this.series = series;
        this.dimensionShort = dimensionShort;
        this.dimensionValue = dimensionValue;
    }

    /**
     * This function is used as a callback to  the function BocResourceManager.poll()
     * This condition determines when to stop polling. If the desired data is found then
     * return 'true' and 'false' otherwise. If the condition returns true, then BocResourceManger stops polling.
     *
     * @param {array} data The data coming from valet
     * @returns {boolean} 'true' if the dimensionShort and dimensionValue were found within the data
     */
    dataFound = (data) => {
        // Loop through the data and look for the presence of the desired dimensionShort and dimensionValue
        for (let index = 0 ; index < data.observations.length ; index++) {
            if (data.observations[index].hasOwnProperty(this.dimensionShort) &&
                data.observations[index][this.dimensionShort] === this.dimensionValue) {
                return true;
            }
        }
        return false;
    };

    /**
     * Polls the series observations from valet until the desired data is found.
     *
     * @param {int} interval Time between polls in milliseconds (default 1 second)
     * @returns {Promise} Returns a promise that will resolve when the resource and desired data are found
     */
    poll(interval = 1000) {
        return BocResourceManager.poll(this.url, interval, this.dataFound);
    }

    /**
     * Creates an instance of BocDataTrigger using the default values from bocVars.dataTrigger.
     *
     * @param {int} interval Time elapsed between polls (Default 1000 milliseconds)
     * @returns {Promise|null} Returns a promise that will resolve when the desired data is found
     */
    static start(interval = 1000) {
        // Return the current promise if it exists, don't create a new one
        if (BocDataTrigger.promise !== null) {
            return BocDataTrigger.promise;
        }

        // Data trigger should only work if the variable bocVars is present
        if (typeof bocVars === 'undefined') {
            return Promise.resolve(0);
        }

        // Data trigger should only work if the variable bocVars.dataTrigger is present
        if (!bocVars.hasOwnProperty('dataTrigger')) {
            // Resolve with a dummy value allowing charts without trigger to render without polling
            return Promise.resolve(0);
        }

        let trigger = new BocDataTrigger(
            bocVars.dataTrigger.series,
            bocVars.dataTrigger.dimensionShort,
            bocVars.dataTrigger.dimensionValue,
            bocVars.dataTrigger.queryParams
        );

        // Start polling
        BocDataTrigger.promise = trigger.poll(interval);

        // When promise is resolved (data is found) reset the promise to null so that start() can be used again
        BocDataTrigger.promise.then(() => {
            BocDataTrigger.promise = null;
        });
        return BocDataTrigger.promise;
    }
}


// Alias for backward compatibility
var BocChartTrigger = BocDataTrigger;

// TODO: Remove this alias once content updates are made to replace 'BocChartTrigger' with 'BocDataTrigger'
